// Investment Analysis: every holding from every investment table, at cost (amount invested),
// the same way the Investments overview adds them up. Live prices are not fetched here.

export const ASSET_CLASSES = [
  { key: 'equity', name: 'Equity', path: '/investments/equity' },
  { key: 'debt', name: 'Debt', path: '/investments/debt' },
  { key: 'gold', name: 'Gold', path: '/investments/gold' },
  { key: 'real-estate', name: 'Real Estate', path: '/investments/real-estate' },
  { key: 'cash-and-liquid', name: 'Cash and Liquid', path: '/investments/cash-and-liquid' },
  { key: 'crypto', name: 'Crypto', path: '/investments/crypto' },
  { key: 'others', name: 'Others', path: '/investments/others' },
];
export const assetClassName = (key) => ASSET_CLASSES.find(c => c.key === key)?.name || key;

// kind: 'amount' (amount_invested), 'price' (real estate: price), 'rd' (monthly_investment × months paid)
export const PRODUCTS = [
  { table: 'stocks_investments', label: 'Stocks', cls: 'equity', path: '/investments/equity/stocks' },
  { table: 'equity_etf_investments', label: 'Equity ETF', cls: 'equity', path: '/investments/equity/equity-etf' },
  { table: 'mutual_fund_equity_investments', label: 'Equity Mutual Fund', cls: 'equity', path: '/investments/equity/mutual-fund-equity' },
  { table: 'nps_investments', label: 'NPS (Equity)', cls: 'equity', path: '/investments/equity/nps' },
  { table: 'govt_bond_investments', label: 'Govt Bonds', cls: 'debt', path: '/investments/debt/govt-bonds' },
  { table: 'corp_bond_investments', label: 'Corporate Bonds', cls: 'debt', path: '/investments/debt/corp-bonds' },
  { table: 'ncd_investments', label: 'NCD', cls: 'debt', path: '/investments/debt/ncd' },
  { table: 'fixed_deposit_investments', label: 'Fixed Deposit', cls: 'debt', path: '/investments/debt/fd' },
  { table: 'recurring_deposit_investments', label: 'Recurring Deposit', cls: 'debt', path: '/investments/debt/recurring-deposit', kind: 'rd' },
  { table: 'debt_mutual_fund_investments', label: 'Debt Mutual Fund', cls: 'debt', path: '/investments/debt/mutual-fund' },
  { table: 'ppf_investments', label: 'PPF', cls: 'debt', path: '/investments/debt/ppf' },
  { table: 'epf_investments', label: 'EPF', cls: 'debt', path: '/investments/debt/epf' },
  { table: 'nps_debt_investments', label: 'NPS (Debt)', cls: 'debt', path: '/investments/debt/nps' },
  { table: 'debt_others_investments', label: 'Debt Others', cls: 'debt', path: '/investments/debt/others' },
  { table: 'gold_bond_investments', label: 'Sovereign Gold Bonds', cls: 'gold', path: '/investments/gold/gold-bonds' },
  { table: 'gold_etf_investments', label: 'Gold ETF', cls: 'gold', path: '/investments/gold/gold-etf' },
  { table: 'digital_gold_investments', label: 'Digital Gold', cls: 'gold', path: '/investments/gold/digital-gold' },
  { table: 'gold_mutual_fund_investments', label: 'Gold Mutual Fund', cls: 'gold', path: '/investments/gold/gold-mutual-fund' },
  { table: 'physical_gold_investments', label: 'Physical Gold', cls: 'gold', path: '/investments/gold/physical-gold' },
  { table: 'reit_investments', label: 'REIT', cls: 'real-estate', path: '/investments/real-estate/reit' },
  { table: 'real_estate_direct_investments', label: 'Property', cls: 'real-estate', path: '/investments/real-estate/real-estate-direct', kind: 'price' },
  { table: 'liquid_etf_investments', label: 'Liquid ETF', cls: 'cash-and-liquid', path: '/investments/cash-and-liquid/liquid-etf' },
  { table: 'mutual_fund_investments', label: 'Liquid Mutual Fund', cls: 'cash-and-liquid', path: '/investments/cash-and-liquid/mutual-fund' },
  { table: 'crypto_investments', label: 'Crypto', cls: 'crypto', path: '/investments/crypto' },
  { table: 'other_investments', label: 'Other Investments', cls: 'others', path: '/investments/others' },
];

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return y ? new Date(y, (m || 1) - 1, d || 1) : null;
};

// RD instalment dates paid so far (same rule as the Investments overview:
// this month's instalment counts once its day has come)
export const rdInstalments = (row, today = new Date()) => {
  const start = parseDate(row.start_date);
  const monthly = Number(row.monthly_investment) || 0;
  if (!start || !monthly) return [];
  const months = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth());
  if (months < 0) return [];
  const count = start.getDate() > today.getDate() ? months : months + 1;
  const dates = [];
  for (let k = 0; k < count; k++) {
    const d = new Date(start.getFullYear(), start.getMonth() + k, 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(start.getDate(), last));
    dates.push({ date: toYmd(d), amount: monthly });
  }
  return dates;
};

const holdingName = (row) => row.name || row.fund_name || row.stock_name || row.ticker || row.symbol || 'Unnamed';

// One flat list of holdings: { product, cls, name, amount, date, flows: [{date, amount}] }
export const toHoldings = (product, rows, today = new Date()) => rows.map((row) => {
  if (product.kind === 'rd') {
    const flows = rdInstalments(row, today);
    return {
      product: product.label, cls: product.cls, path: product.path, name: holdingName(row),
      amount: flows.reduce((s, f) => s + f.amount, 0), date: row.start_date, flows,
    };
  }
  const amount = Number(product.kind === 'price' ? row.price : row.amount_invested) || 0;
  const date = row.purchase_date || null;
  return {
    product: product.label, cls: product.cls, path: product.path, name: holdingName(row),
    amount, date, flows: date ? [{ date: String(date).slice(0, 10), amount }] : [],
  };
});

// Buckets for the timeline: months of a period, or years for "all"
export const yearBuckets = (firstYear, today = new Date()) => {
  const buckets = [];
  for (let y = firstYear; y <= today.getFullYear(); y++) {
    buckets.push({ key: String(y), label: String(y), longLabel: String(y), from: `${y}-01-01`, to: `${y}-12-31` });
  }
  return buckets;
};

export const monthBuckets = (months) => months.map((m) => {
  const last = new Date(m.year, m.month + 1, 0).getDate();
  return { key: m.key, label: m.label, longLabel: m.longLabel, from: `${m.key}-01`, to: `${m.key}-${pad(last)}` };
});

// Everything the Investments tab shows
// groupBy: 'cls' (split each bucket by asset class) or 'product' (by product, inside one class)
export const analyseInvestments = (holdings, savingsBank, buckets, sales, groupBy = 'cls') => {
  const byClass = {};
  const byProduct = {};
  holdings.forEach((h) => {
    if (!h.amount) return;
    byClass[h.cls] = byClass[h.cls] || { cls: h.cls, name: assetClassName(h.cls), total: 0, count: 0 };
    byClass[h.cls].total += h.amount;
    byClass[h.cls].count += 1;
    const key = `${h.cls}|${h.product}`;
    byProduct[key] = byProduct[key] || { cls: h.cls, product: h.product, path: h.path, total: 0, count: 0, first: null };
    byProduct[key].total += h.amount;
    byProduct[key].count += 1;
    if (h.date && (!byProduct[key].first || h.date < byProduct[key].first)) byProduct[key].first = String(h.date).slice(0, 10);
  });
  if (savingsBank > 0) {
    byClass['cash-and-liquid'] = byClass['cash-and-liquid'] || { cls: 'cash-and-liquid', name: assetClassName('cash-and-liquid'), total: 0, count: 0 };
    byClass['cash-and-liquid'].total += savingsBank;
    byProduct['cash-and-liquid|Savings Bank'] = { cls: 'cash-and-liquid', product: 'Savings Bank', path: '/investments/cash-and-liquid/savings-bank', total: savingsBank, count: 0, first: null, balance: true };
  }

  const total = Object.values(byClass).reduce((s, c) => s + c.total, 0);
  const classes = Object.values(byClass).sort((a, b) => b.total - a.total);
  classes.forEach((c) => { c.share = total > 0 ? c.total / total : 0; });
  const products = Object.values(byProduct).sort((a, b) => b.total - a.total);
  products.forEach((p) => { p.share = total > 0 ? p.total / total : 0; });

  // Timeline: money put in per bucket (by class), and the running total at each bucket's end
  const flows = holdings.flatMap(h => h.flows.map(f => ({ ...f, group: groupBy === 'product' ? h.product : h.cls })));
  const rows = buckets.map((b) => {
    const inBucket = flows.filter(f => f.date >= b.from && f.date <= b.to);
    const perGroup = {};
    inBucket.forEach((f) => { perGroup[f.group] = (perGroup[f.group] || 0) + f.amount; });
    const salesIn = (sales || []).filter(s => s.sale_date && String(s.sale_date).slice(0, 10) >= b.from && String(s.sale_date).slice(0, 10) <= b.to);
    return {
      ...b,
      invested: inBucket.reduce((s, f) => s + f.amount, 0),
      perGroup,
      cumulative: flows.filter(f => f.date <= b.to).reduce((s, f) => s + f.amount, 0),
      realised: salesIn.reduce((s, x) => s + (Number(x.profit_loss) || 0), 0),
      salesCount: salesIn.length,
    };
  });
  const periodFrom = buckets[0]?.from;
  const periodTo = buckets[buckets.length - 1]?.to;
  const undated = holdings.filter(h => h.amount && !h.flows.length).reduce((s, h) => s + h.amount, 0);

  return {
    total,
    holdingsCount: holdings.filter(h => h.amount).length,
    classes,
    products,
    rows,
    investedInPeriod: rows.reduce((s, r) => s + r.invested, 0),
    realisedInPeriod: rows.reduce((s, r) => s + r.realised, 0),
    salesInPeriod: rows.reduce((s, r) => s + r.salesCount, 0),
    undated,
    savingsBank,
    periodFrom,
    periodTo,
  };
};
